import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  selectHome,
  selectMustWinView,
  selectMustWinFilterOptions,
  selectMustWinDropdownSelection
} from 'app/pages/Home/slice/selectors';
import {
  selectTimelineViewMode,
  selectTimelineIconsVisible
} from 'app/components/MonthlyTimeline/store/selectors';
import type { ViewStateParams } from 'utils/viewStateDeepLink';

/**
 * Builds a full ViewStateParams snapshot of the current Redux view state
 * (filters, date range, view toggles, Must Wins, My Stuff, timeline mode).
 * Shared by the Share View / Save View flow (Home/index.tsx) and the
 * Saved Views shared-preferences stash/restore mechanism
 * (useSavedViewActions.ts) so both read from one definition.
 */
export function useCurrentViewState() {
  const homeState = useSelector(selectHome);
  const mustWinViewActive = useSelector(selectMustWinView);
  const mustWinFilterOptions = useSelector(selectMustWinFilterOptions);
  const mustWinDropdownSelection = useSelector(selectMustWinDropdownSelection);
  const timelineViewMode = useSelector(selectTimelineViewMode);
  const timelineIconsVisible = useSelector(selectTimelineIconsVisible);

  return useCallback((): ViewStateParams => {
    const mustWinState: Pick<
      ViewStateParams,
      | 'mustWin'
      | 'mustWinType'
      | 'mustWinValue'
      | 'mustWinLabel'
      | 'mustWinFilters'
    > = {
      mustWin: mustWinViewActive
    };

    if (mustWinViewActive && mustWinDropdownSelection) {
      const selectedItem = mustWinFilterOptions.data?.find(
        (item: { value: string }) => item.value === mustWinDropdownSelection
      );

      if (selectedItem) {
        mustWinState.mustWinValue = selectedItem.value;
        mustWinState.mustWinLabel = selectedItem.label;

        if (selectedItem.type === 'multi') {
          mustWinState.mustWinType = 'multi';
          mustWinState.mustWinFilters = selectedItem.filters;
        } else {
          mustWinState.mustWinType = selectedItem.type;
        }
      }
    }

    return {
      startDate: homeState.selectedDates?.start,
      endDate: homeState.selectedDates?.end,
      productGroups: homeState.productGroups.selectedGroups,
      categories: homeState.categories.selectedCats,
      audiences: homeState.audiences.selectedAudiences,
      events: homeState.events.selectedEvents,
      features: homeState.features.selectedFeatures,
      status: homeState.status.selectedStatus,
      tiers: homeState.lanesTiers.selectedLanesTiers,
      products: homeState.productFilter.selectedProducts,
      genAiFilters: homeState.genAiFilter.selectedFilters,
      revenueImpactFilters: homeState.revenueImpactFilter.selectedFilters,
      momentTypeFilters: homeState.momentTypeFilter.selectedMomentTypes,
      categoryProductCategories:
        homeState.categoryProductFilter.selectedCategories,
      categoryProductProducts: homeState.categoryProductFilter.selectedProducts,
      isCompact: homeState.isCompact,
      showParkingLot: homeState.parkingLot,
      collapseAll: homeState.collapseAll,
      unNestAll: homeState.unNestAll,
      ...mustWinState,
      myStuff: homeState.isMyStuffViewActive,
      timelineViewMode,
      timelineIconsVisible,
      // Include active issueId filter so Share View preserves it
      issueIds:
        homeState.searchFilterActive &&
        homeState.searchFilteredItems.length > 0 &&
        homeState.searchFilteredItems.every((id: string) => /^ARM-\d+/.test(id))
          ? homeState.searchFilteredItems
          : undefined
    };
  }, [
    homeState.selectedDates,
    homeState.productGroups.selectedGroups,
    homeState.categories.selectedCats,
    homeState.audiences.selectedAudiences,
    homeState.events.selectedEvents,
    homeState.features.selectedFeatures,
    homeState.status.selectedStatus,
    homeState.lanesTiers.selectedLanesTiers,
    homeState.productFilter.selectedProducts,
    homeState.genAiFilter.selectedFilters,
    homeState.revenueImpactFilter.selectedFilters,
    homeState.momentTypeFilter.selectedMomentTypes,
    homeState.categoryProductFilter.selectedCategories,
    homeState.categoryProductFilter.selectedProducts,
    homeState.isCompact,
    homeState.parkingLot,
    homeState.collapseAll,
    homeState.unNestAll,
    homeState.isMyStuffViewActive,
    homeState.searchFilterActive,
    homeState.searchFilteredItems,
    mustWinViewActive,
    mustWinDropdownSelection,
    mustWinFilterOptions.data,
    timelineViewMode,
    timelineIconsVisible
  ]);
}
